// janus-engine/actions/cast-range-gate.ts — the cast-time RANGE / GEOMETRY vetoes validate_and_adjust_targets() still ran only in PHP
// (cast-gate session, 2026-09-29; plans/janus-session-cast-gate-handoff-2026-09-29.md). A sub-module of the P4 gate
// (ability-preconditions.ts): pure — every page read comes through the readers.
//
// BOUNCING SPELL: an armed, eligible cast at exactly two creature targets needs the caster in range of the PRIMARY and the SECONDARY
// within 10 ft of it. This is the ONLY judge of that geometry — PHP's twin in validate_and_adjust_targets() is deleted (R-103,
// 2026-09-29; archived in _ai_code_files/retired_php/cast_range_geometry/). Distances are the FRONT-END combat distance
// (ft3d-combat-distance-v3 — the page's rule, owner 2026-09-21; never stricter than the retired PHP v2 measure). The verdict:
//   refuse  — the pair is illegal: refused with the retired PHP gate's exact message (the range number is the v3 distance);
//   proven  — the pair is legal;
//   unknown — something the page cannot measure (a target it cannot see clearly — PHP's visibility filter still drops those — a
//             missing size, a range modifier such as Far Bender / Distant / cast-through-summon that only ever EXTENDS range): the cast
//             posts, since the retired gate is no longer there to be stricter;
//   not-bounce — this cast is not a Bouncing cast at two targets (nothing to judge here).
// Census + frozen PHP oracle: tools/test/janus-cast-range-placement-refusal-inventory.mjs (fixture php-verdicts-2026-09-29.json).
import { combatCenter, combatDistanceFt, combatSizeFt, type AbilityPreconditionReaders } from './ability-preconditions';
import { isOperableObject, itemTypeGateAdmits, OPERABLE_OBJECT_LAYER } from '../../shared/ability-targeting-contract';
import { AuraOwnerGateUnavailable, isDormantOwnerGatedAura, isHostileAuraSelfAnchor } from '../compile/creature-status-reader';
import { createSkillRequirementChecker } from '../compile/skill-requirements';
import { pageSkillEntries } from '../../shared/page-skill-entries';
import { fastNextSpell, nextSpellCarriers, nextSpellSingleTarget, nextSpellRangeId } from '../sanguine/next-spell.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;

const SPELL_MELEE_ATTACK_ROLL = '5740CA49-006E-A7A0-B46B-6DDED6CD546C';
const SPELL_RANGED_ATTACK_ROLL = 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C';
const RANGE_SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const RANGE_REACH = '36D59FF4-6778-96ED-8664-F5D63638C77B';
const RANGE_UNLIMITED = 'BB47D2E8-FFFD-8A04-FC6C-FC02583750ED';
/** RangeType x_distance (includes/additions.php RangeType::LIST); close / near / far / very far. */
export const RANGE_FT: Readonly<Record<string, number>> = {
  '4C761395-2532-3F23-7CBA-CA81AF5C0855': 30, 'A95F219A-6288-6B17-6DB7-03DE4B97AE96': 60,
  'D1E8DA38-61C2-97AA-6173-4BF6157FEEE3': 120, 'F6DAE84B-CBB3-5C0F-89EF-370DE841DFDA': 240,
};
/** ft_sanguine_next_spell_bounce_eligible()'s slow casting times. */
const SLOW_CASTS = new Set(['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours']);

export const BOUNCE_NEEDS_CREATURES = 'Bouncing Spell needs two creature targets.';
export const BOUNCE_GAP = 'The bounce target must be within 10 ft of the primary target (Bouncing Spell).';

export type CastGeometryVerdict =
  | { kind: 'not-bounce' }
  | { kind: 'refuse'; message: string }
  | { kind: 'proven' }
  | { kind: 'unknown'; site: string };

export interface CastGeometryReaders extends AbilityPreconditionReaders {
  /** The page's perception of `target` from the caster (janus-engine/ability/visibility.ts); null when unjudgeable. */
  visibility: (caster: Row, target: Row) => string | null;
}

export interface BounceInput {
  caster: Row;
  ability: Row;
  /** The posted selection split on ',' (primary first — the client's comma order). */
  tokens: string[];
  /** Each token's page row (a roster member for a creature id), null when the page does not hold it. */
  targets: Array<Row | null>;
  variable1?: unknown;
  /** D-050: the encounter roster (cast-through-summon's controlled summons); absent = a through-summon caster stays unknown. */
  roster?: readonly Row[];
}

const has = (read: AbilityPreconditionReaders, row: Row | null, key: string): boolean => !!row && read.has(row, key);
const present = (value: unknown): boolean => value !== null && value !== undefined && value !== '' && value !== 0 && value !== '0' && value !== false;

/** ft_sanguine_next_spell_bounce_eligible() over page rows (a browser cast is never a trigger dispatch). */
export function bounceEligible(caster: Row, ability: Row, variable1: unknown, read: AbilityPreconditionReaders): boolean {
  return nextSpellSingleTarget(read, ability, variable1, true)
    && nextSpellCarriers(read, caster).some(c => c.layers.includes('next_spell_bounce_cast'));
}

/** ft_sanguine_next_spell_twin_extra_targets() > 0: an armed Twin carrier + a direct single-target spell (no attack-roll requirement). */
export function twinEligible(caster: Row, ability: Row, variable1: unknown, read: AbilityPreconditionReaders): boolean {
  return nextSpellSingleTarget(read, ability, variable1)
    && nextSpellCarriers(read, caster).some(c => c.layers.includes('next_spell_twin_cast'));
}

/** validate_and_adjust_targets()'s $ft_extra_targets: Twin + Bouncing each allow ONE more target on a single-target spell, and they
 *  do not stack (owner 2026-07-22: capped at +1). The FT3D targeting profile raises its ceiling by this. */
export function sanguineExtraTargets(caster: Row, ability: Row, variable1: unknown, read: AbilityPreconditionReaders): 0 | 1 {
  return twinEligible(caster, ability, variable1, read) || bounceEligible(caster, ability, variable1, read) ? 1 : 0;
}

const NO_KEYS: ReadonlySet<string> = new Set();
const ABILITY_BUCKETS =['passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability'];
/** Status definitions are providers only where their payload reaches this holder. Unknown definitions/gates stay unproved.
 *  `evaluated` (Wizard v9, 2026-10-09): condition keys the CALLER's `matches` judges itself (if_circle_of_magic_cast via spell-circle-scope.ts),
 *  and the class-grant requirements (requires_class / requires_subclass / requires_class_level) a SELF-applied status already met when its
 *  passive was granted (Avatar of Evocation's combat-start self-status) — those keys no longer make the status unprovable. Opt-in per caller. */
function* statusLayerProviders(read: AbilityPreconditionReaders, caster: Row, layer: string, evaluated: ReadonlySet<string> = NO_KEYS): Generator<Row | undefined> {
  for (const status of read.statuses(caster)) {
    if (Number(scalar(status.status_deleted) ?? 0) !== 0 || status.passive_conditions_active === false) continue;
    const inline = record(status.status_arr);
    const def = inline && (inline.item_layer_types != null || inline.layers != null || inline.computed != null)
      ? inline : read.resolveItem?.(status.status_item_id ?? status.status_arr);
    if (!def) { yield undefined; return; }
    if (!present(read.value(def, layer))) continue;
    const casterId = status.status_caster_id === undefined ? status.status_target_id : status.status_caster_id;
    if (isHostileAuraSelfAnchor(def, status.status_aoe_source, casterId, status.status_target_id,
      (row, key) => read.value(record(row), key))) continue;
    try {
      if (isDormantOwnerGatedAura({ ...status, status_arr: def, status_caster_id: casterId },
        (row, key) => read.value(record(row), key), undefined, caster)) continue;
    } catch (error) {
      if (!(error instanceof AuraOwnerGateUnavailable)) throw error;
      yield undefined; return;
    }
    if (present(read.value(def, 'buff_does_not_layer_onto_opposing_team'))) {
      const ownerId = upper(scalar(casterId)), holderId = upper(scalar(record(caster.encounter_creatures)?.encounter_creature_id));
      if (ownerId && ownerId !== holderId) {
        const roster = record(caster.encounter_arr)?.encounter_creatures_arr;
        const owner = roster && typeof roster === 'object' ? Object.values(roster).map(record)
          .find(row => upper(scalar(record(row?.encounter_creatures)?.encounter_creature_id)) === ownerId) : null;
        if (!owner) { yield undefined; return; }
        const team = (row: Row): string => {
          const raw = scalar(record(row.encounter_creatures)?.team) ?? scalar(read.value(row, 'team'));
          return upper(scalar(record(record(raw)?.items)?.item_id) ?? raw);
        };
        const teams = ['7DBEB6D4-D147-703A-022E-05B290BB95C3', '9399856B-2132-7574-1684-91139A2831DD'];
        if (teams.includes(team(owner)) && teams.includes(team(caster)) && team(owner) !== team(caster)) continue;
      }
    }
    const selfApplied = upper(scalar(casterId)) !== '' && upper(scalar(casterId)) === upper(scalar(record(caster.encounter_creatures)?.encounter_creature_id));
    if (Object.keys({ ...(record(def.item_layer_types) ?? {}), ...(record(def.layers) ?? {}) })
      .some(key => !(evaluated.has(key) && (key.startsWith('if_') || selfApplied))
        && ((key.startsWith('if_') && key !== 'if_aura_owner_has_status_effect_active') || key.startsWith('requires_')))) {
      yield undefined; return;
    }
    yield def;
  }
}
/**
 * find_caster_layer_provider_arr() as a CAN-NOT-RULE-OUT test: true when the caster, an active ability bucket entry, a feat or a feat's
 * granted ability or payload status carries `layer` — and ALSO when a definition the page does not hold could.
 */
export function casterMayProvide(read: AbilityPreconditionReaders, caster: Row, layer: string): boolean {
  if (!read.entries) return true;
  const carries = (row: Row | null): boolean => !!row && present(read.value(row, layer));
  const resolve = (entry: unknown): Row | null | undefined => {
    const inner = record(record(entry)?.item_arr) ?? record(entry);
    if (inner && (record(inner.item_layer_types) || record(inner.layers))) return inner;
    return read.resolveItem ? read.resolveItem(entry) : undefined;
  };
  if (carries(caster)) return true;
  for (const bucket of ABILITY_BUCKETS) {
    for (const entry of read.entries(caster, bucket)) {
      if (record(entry)?.passive_conditions_active === false) continue;
      const ability = resolve(entry);
      if (!ability) return true;
      // find_caster_layer_provider_arrs() skips a combat-start TIMED passive's bucket copy (2026-10-07, Thief L40): it provides only through
      // the status row its trigger writes for the window, which the status walk below reads. (No aura skip: that walk has none.)
      if (bucket === 'passive_ability' && combatStartTimedPassive(read, ability)) continue;
      if (carries(ability)) return true;
    }
  }
  for (const featEntry of read.entries(caster, 'feat')) {
    const feat = resolve(featEntry);
    if (!feat) return true;
    if (carries(feat)) return true;
    for (const bucket of ABILITY_BUCKETS) {
      for (const granted of read.entries(feat, bucket)) {
        const def = resolve(granted);
        if (!def) return true;
        // a feat-granted combat-start TIMED passive is skipped too (find_caster_layer_provider_arrs' grant descent; Codex review 2026-10-07)
        if (bucket === 'passive_ability' && combatStartTimedPassive(read, def)) continue;
        if (carries(def)) return true;
      }
    }
  }
  for (const status of statusLayerProviders(read, caster, layer)) if (!status || carries(status)) return true;
  return false;
}

/** check_item_arr_aura_effect() || (triggers_after_combat_begins && duration_type): a passive whose effect reaches its holder only through
 *  the status row it writes (build_caster_roll_type_modifiers.php's bucket skip). */
function statusDeliveredPassive(read: AbilityPreconditionReaders, ability: Row): boolean {
  const aura = ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(l => present(read.value(ability, l)));
  return aura || combatStartTimedPassive(read, ability);
}
/** ft_passive_is_combat_start_timed() (heal_target_arr.php): triggers_after_combat_begins + a duration ("for 1 minute after combat begins"). */
function combatStartTimedPassive(read: AbilityPreconditionReaders, ability: Row): boolean {
  return present(read.value(ability, 'triggers_after_combat_begins')) && present(read.value(ability, 'duration_type'));
}

/** ability_has_runtime_conditions()'s keys on a feat (non-empty values only): if_*, requires_skill_tier_N and three named layers. Every other
 *  requires_* (requires_class / _ancestry / _min_essence_* / _level / _subclass / _lineage — 54 live feats, 2026-10-14 census
 *  tools/probes/feat_gate_provider_census.php) is a PICK-TIME prerequisite: check_ability_conditions() passes a feat carrying only those, and
 *  the compile already honoured them by granting the feat. Reading them as a gate refused e.g. Familiar's Remote Bond's cast-through-summon. */
const RUNTIME_GATE_LAYERS = new Set(['requires_equipped_item', 'draw_sheathed_weapon_for_attack_ability', 'ability_active_if_ability_charge_is_at_median']);
const runtimeGateKey = (key: string): boolean => key.startsWith('if_') || key.startsWith('requires_skill_tier_') || RUNTIME_GATE_LAYERS.has(key);
/** The def's runtime-gate layers over every surface ability_has_runtime_conditions() scans (item_layer_types and the bare row — legacy /
 *  pre-reshape tolerance) plus the TS-only `layers` shape; empty values are skipped, as PHP skips them. */
function featGateLayers(def: Row): Row {
  const filled = (value: unknown): boolean => value !== null && value !== undefined && value !== ''
    && !(Array.isArray(value) && value.length === 0) && !(record(value) && Object.keys(value as Row).length === 0);
  // each surface is scanned on its own (as PHP does): an empty copy on one surface never erases a filled gate on another
  const out: Row = {};
  for (const surface of [record(def.item_layer_types), record(def.layers), def]) {
    for (const [key, value] of Object.entries(surface ?? {})) if (!(key in out) && runtimeGateKey(key) && filled(value)) out[key] = value;
  }
  return out;
}
function featRuntimeGateKeys(def: Row): string[] {
  return Object.keys(featGateLayers(def));
}

/** check_skill_requirements() for a feat container over the caster's compiled skills; null = the page cannot judge it (no skill registry, no
 *  layer reader, or a caster row without computed.creature.skills — never read as tier 0). The registry is the reader's when it supplies one,
 *  else the page's SKILL_CATALOG — so every casterLayerProvider caller judges the gate, not only the cast-range readers. */
function featSkillTierGate(read: AbilityPreconditionReaders, feat: Row, caster: Row): boolean | null {
  const skillEntries = read.skillEntries?.length ? read.skillEntries : pageSkillEntries();
  if (!skillEntries.length || !read.entries || !record(record(record(caster.computed)?.creature)?.skills)) return null;
  const entries = read.entries;
  // the checker enumerates item_layer_types only (else the bare row): a requirement authored on the bare row or in the `layers` shape is
  // folded into it first — otherwise the checker sees no requirement and passes the feat (Codex reviews 2026-10-14)
  const gateRow = { ...feat, item_layer_types: { ...(record(feat.item_layer_types) ?? {}), ...featGateLayers(feat) } };
  return createSkillRequirementChecker({ skillEntries, layerEntries: (row, key) => entries(row as Row | null, key),
    catalogRows: {}, abilityTypes: [], aggregationStrategy: () => 'list', resolveCarrierRef: () => null, resolveInventoryInstance: () => null })(gateRow, caster);
}

/**
 * find_caster_layer_provider_arr() — the FIRST row carrying `layer` in PHP's walk order (the caster, its active ability buckets, its feats and
 * their granted abilities, then active payload statuses). null = none; undefined = an entry the page cannot resolve might be the provider.
 */
export function casterLayerProvider(read: AbilityPreconditionReaders, caster: Row, layer: string,
  matches: (provider: Row) => boolean = () => true,
  /** condition keys `matches` evaluates itself (statusLayerProviders' doc) — the feat gate skips the if_* ones too */
  evaluated: ReadonlySet<string> = NO_KEYS): Row | null | undefined {
  if (!read.entries) return undefined;
  const carries = (row: Row | null): boolean => !!row && present(read.value(row, layer)) && matches(row);
  const resolve = (entry: unknown): Row | null | undefined => {
    const inner = record(record(entry)?.item_arr) ?? record(entry);
    if (inner && (record(inner.item_layer_types) || record(inner.layers))) return inner;
    return read.resolveItem ? read.resolveItem(entry) : undefined;
  };
  // check_ability_conditions() on a feat CONTAINER (its own runtime gate): a gated feat grants nothing when it fails. Only
  // ability_has_runtime_conditions()'s keys gate at runtime (featRuntimeGateKeys); the skill-tier prerequisite is judged from the caster's
  // compiled skills — Spell Sniper's Bending tier 2 made Far Bender unprovable and refused every Guiding Bolt (2026-10-14). Any other
  // runtime gate is not evaluated here, so a gated feat that might provide stays unprovable (Codex D-050 review P2).
  // `evaluated` keys are judged by `matches` on the row it is handed: they exempt the feat's OWN provision only — a granted ability is
  // matched without the feat, so on the grant descent the feat's evaluated key is an unevaluated gate (Codex review 2026-10-14)
  const featGate = (def: Row, own: boolean): 'open' | 'closed' | 'unknown' => {
    const keys = featRuntimeGateKeys(def).filter(key => !(own && key.startsWith('if_') && evaluated.has(key)));
    if (!keys.length) return 'open';
    const tiers = keys.filter(key => key.startsWith('requires_skill_tier_'));
    const skills = tiers.length ? featSkillTierGate(read, def, caster) : null;
    if (skills === false) return 'closed'; // check_ability_conditions() ANDs its gates: one failed prerequisite closes the feat
    return skills === true && tiers.length === keys.length ? 'open' : 'unknown';
  };
  if (carries(caster)) return caster;
  for (const bucket of ABILITY_BUCKETS) {
    for (const entry of read.entries(caster, bucket)) {
      // find_caster_layer_provider_arrs() reads only COMPILED (array) bucket entries — a bare id reference is skipped there
      const row = record(entry);
      if (!row || row.passive_conditions_active === false) continue;
      const ability = resolve(entry);
      if (!ability) return undefined;
      // a combat-start TIMED passive ("for 1 minute after combat begins") and an AURA passive reach their holder as a STATUS row — the
      // status walk below finds them only while held; the bucket copy would provide before combat and after expiry (2026-10-07,
      // Spellblade v8 "Master of the Grimoire"; build_caster_roll_type_modifiers' bucket skip is the PHP precedent)
      if (bucket === 'passive_ability' && statusDeliveredPassive(read, ability)) continue;
      if (carries(ability)) return ability;
    }
  }
  for (const featEntry of read.entries(caster, 'feat')) {
    const feat = resolve(featEntry);
    if (!feat) return undefined;
    const ownGate = featGate(feat, true), grantGate = featGate(feat, false);
    if (ownGate === 'closed' || grantGate === 'closed') continue; // a failed skill tier closes the feat whole (never an evaluated key)
    if (carries(feat)) {
      if (ownGate === 'unknown') return undefined;
      return feat;
    }
    if (grantGate === 'unknown') {
      const could = ABILITY_BUCKETS.some(bucket => read.entries!(feat, bucket).some(granted => { const def = resolve(granted); return !def || carries(def); }));
      if (could) return undefined;
      continue;
    }
    for (const bucket of ABILITY_BUCKETS) {
      for (const granted of read.entries(feat, bucket)) {
        const def = resolve(granted);
        if (!def) return undefined;
        if (bucket === 'passive_ability' && combatStartTimedPassive(read, def)) continue; // the grant-descent twin of the bucket skip
        if (carries(def)) return def;
      }
    }
  }
  for (const status of statusLayerProviders(read, caster, layer, evaluated)) {
    if (!status) return undefined;
    if (carries(status)) return status;
  }
  return null;
}

/** RangeType x_distance as check_cast_spell_through_summon_range()'s get_distance reads it: reach 0, unlimited ∞, the four distances. */
const THROUGH_SUMMON_X: Record<string, number> = { ...RANGE_FT, [RANGE_REACH]: 0, [RANGE_UNLIMITED]: Number.POSITIVE_INFINITY };

/**
 * D-050 (owner 2026-09-30): is_target_in_range()'s CAST-THROUGH-SUMMON block — the summons whose position may serve as this spell's range
 * origin (the closest one to the target is measured from, when strictly closer than the caster). Gates, in PHP's order: a provider of
 * `cast_spell_through_summon_range` with a UUID value; check_cast_spell_through_summon_range() (the spell's RangeType distance ≤ the
 * provider's); a spell; `count_caster_summons`. The summons are the roster rows the caster CONTROLS (build_caster_encounter_creature_summons_arr:
 * controller_encounter_creature_id). null = the block does not apply; undefined = the page cannot prove it (a provider it cannot resolve, a
 * RangeType it does not know, or a summon it does not hold).
 */
export function throughSummonOrigins(caster: Row, ability: Row, roster: readonly Row[], read: AbilityPreconditionReaders,
  /** The range the cast measures with (effectiveSpellRangeId — janus.php raises computed.range BEFORE is_target_in_range checks eligibility). */
  rangeId?: string): Row[] | null | undefined {
  // PHP's gate is one side-effect-free AND, so the terms the page can always decide go FIRST: a non-spell (a menta — Meurtez's Cogni Slam
  // was refused "cast-through-summon" 2026-09-30) or a caster with no summons never applies, whatever the provider walk could prove.
  if (itemType(ability) !== 'spell') return null;
  // the count is an encounter_creatures COLUMN (fetch_encounter_creatures' subquery), not a layer — a layer read alone is always empty, which
  // left the whole block dead in both lanes until 2026-09-30 (cast-range-non-spell-summoner-staging-smoke)
  const count = Math.trunc(Number(scalar(read.value(caster, 'count_caster_summons')) ?? scalar(record(caster.encounter_creatures)?.count_caster_summons) ?? 0) || 0);
  if (count <= 0) return null;
  const provider = casterLayerProvider(read, caster, 'cast_spell_through_summon_range');
  if (provider === undefined) return undefined;
  if (provider === null) return null;
  const providerRange = upper(scalar(read.value(provider, 'cast_spell_through_summon_range')));
  if (!UUID.test(providerRange)) return null;
  const abilityRange = rangeId ?? scalar(read.value(ability, 'range'));
  if (abilityRange === null || abilityRange === undefined) return null;
  // RangeType::getById on a bare number (or any id it does not list) → null → check_cast_spell_through_summon_range() is false
  if (/^\s*\d+(\.\d+)?\s*$/.test(String(abilityRange))) return null;
  const casterX = THROUGH_SUMMON_X[providerRange], itemX = THROUGH_SUMMON_X[upper(abilityRange)];
  if (casterX === undefined || itemX === undefined) return upper(abilityRange) === RANGE_SELF ? null : undefined;
  if (!(itemX <= casterX)) return null;
  const casterId = upper(record(caster.encounter_creatures)?.encounter_creature_id);
  const summons = roster.filter(row => {
    const ec = record(row.encounter_creatures);
    return !!ec && upper(ec.controller_encounter_creature_id) === casterId && casterId !== '' && Number(scalar(ec.encounter_creature_deleted) ?? 0) !== 1;
  });
  // Completeness is judged on THIS encounter's summons: one parked in another encounter is not on this map and is never an origin, but the
  // count spans every encounter (fetch_encounter_creatures' subquery has no encounter filter). The baked caster_summons list names each
  // summon's encounter (2026-09-30); a bake without it (older blob) keeps the count comparison.
  const encounterId = upper(record(caster.encounter_creatures)?.encounter_id);
  const baked = record(caster.encounter_creatures)?.caster_summons;
  const bakedRows = Array.isArray(baked) ? baked.map(record) : null;
  if (encounterId && bakedRows && bakedRows.length && bakedRows.every(r => !!r && typeof r.encounter_id === 'string' && r.encounter_id !== '')) {
    const held = new Set(summons.map(row => upper(record(row.encounter_creatures)?.encounter_creature_id)));
    const local = bakedRows.filter(r => upper(r!.encounter_id) === encounterId).map(r => upper(r!.encounter_creature_id));
    return local.every(id => held.has(id)) ? summons : undefined;
  }
  // an older bake: one the page does not hold could be the closest
  return summons.length < count ? undefined : summons;
}

/** ft_sanguine_next_spell_range_multiplier(): the armed Distant-family statuses' product (each > 1), clamped to [1, 10]; 1 for a non-spell,
 *  a slow cast, or a trigger-dispatched cast (the caller passes `triggerFrame`). */
export function spellRangeMultiplier(caster: Row, ability: Row, read: AbilityPreconditionReaders, triggerFrame = false): number {
  if (triggerFrame || !fastNextSpell(read, ability)) return 1;
  let product = 1;
  for (const { status, definition } of nextSpellCarriers(read, caster)) {
    const multiplier = Math.trunc(Number(scalar(read.value(definition, 'next_spell_range_multiplier')) ?? 0) || 0);
    if (multiplier > 1 && String(scalar(status.status_id) ?? '') !== '') product *= multiplier;
  }
  return Math.max(1, Math.min(10, product));
}

/** Permanent spell range applies to slow casts and trigger frames too; Self is never extended. */
export function persistentSpellRangeMultiplier(caster: Row, ability: Row, read: AbilityPreconditionReaders): number | undefined {
  if (itemType(ability) !== 'spell' || upper(read.value(ability, 'range')) === RANGE_SELF) return 1;
  const provider = casterLayerProvider(read, caster, 'spell_range_multiplier');
  if (provider === undefined) return undefined;
  if (provider === null) return 1;
  const value = Number(scalar(read.value(provider, 'spell_range_multiplier')));
  return Number.isFinite(value) && Number.isInteger(value) ? Math.max(1, Math.min(10, value)) : undefined;
}

/** janus.php's FAR BENDER upgrade (the spell ranged attack roll's range raised from a provider, before validation): the range id the cast
 *  measures with. The provider's `…_increased_from` sets the floor (Close = 30 when unset); only ever an increase. undefined = unprovable. */
export function effectiveSpellRangeId(caster: Row, ability: Row, read: AbilityPreconditionReaders): string | undefined {
  const authored = nextSpellRangeId(read, caster, ability);
  if (itemType(ability) !== 'spell' || upper(read.value(ability, 'roll_type')) !== SPELL_RANGED_ATTACK_ROLL) return authored;
  const provider = casterLayerProvider(read, caster, 'spell_ranged_attack_roll_range_increased_to');
  if (provider === undefined) return undefined;
  if (provider === null) return authored;
  const x = (id: unknown): number | null | undefined => { const key = upper(scalar(id)); if (key === '') return null; return RANGE_FT[key] ?? (key === RANGE_REACH || key === RANGE_UNLIMITED || key === RANGE_SELF ? null : undefined); };
  const to = x(read.value(provider, 'spell_ranged_attack_roll_range_increased_to')), cur = x(authored);
  const fromRaw = x(read.value(provider, 'spell_ranged_attack_roll_range_increased_from'));
  if (to === undefined || cur === undefined || fromRaw === undefined) return undefined;
  const floor = fromRaw ?? 30;
  return to !== null && cur !== null && cur >= floor && to > cur ? upper(scalar(read.value(provider, 'spell_ranged_attack_roll_range_increased_to'))) : authored;
}

/** PHP round($x, 1) for a positive shortfall, printed as PHP prints a float ("17.5", "20"). */
const shortBy = (x: number): string => String(Math.round((x + Number.EPSILON) * 10) / 10);

/** is_target_in_range()['warning'] for the caster → primary leg, measured v3; undefined when the page cannot judge it. D-050 (owner
 *  2026-09-30): the range extensions are the front end's — the Distant multiplier (spellRangeMultiplier), Far Bender's upgraded range
 *  (effectiveSpellRangeId) and cast-through-summon (the closest controlled summon measures when strictly closer: throughSummonOrigins). */
export function primaryRangeRefusal(caster: Row, primary: Row, ability: Row, read: AbilityPreconditionReaders, roster: readonly Row[] | undefined): string | null | undefined {
  // a move-towards rider extends reach (not modelled here)
  if (present(read.value(ability, 'caster_moves_towards_target_in_ft')) || record(ability.attack_ability_arr)) return undefined;
  if (has(read, caster, 'swarm_width_lanes') || has(read, primary, 'swarm_width_lanes')) return undefined;
  const range = effectiveSpellRangeId(caster, ability, read);
  if (range === undefined) return undefined;
  const persistent = persistentSpellRangeMultiplier(caster, ability, read);
  if (persistent === undefined) return undefined;
  const multiplier = Math.min(10, persistent * spellRangeMultiplier(caster, ability, read));
  const casterSize = combatSizeFt(caster, read);
  const from = combatCenter(caster), to = combatCenter(primary);
  if (casterSize === null || !from || !to) return undefined;
  let distance = combatDistanceFt(from, to, casterSize, combatSizeFt(primary, read));
  let measuring = caster;
  const summons = throughSummonOrigins(caster, ability, roster ?? [], read, range);
  if (summons === undefined || (summons !== null && roster === undefined)) return undefined;
  for (const summon of summons ?? []) {
    const size = combatSizeFt(summon, read), at = combatCenter(summon);
    if (size === null || !at || has(read, summon, 'swarm_width_lanes')) return undefined;
    const d = combatDistanceFt(at, to, size, combatSizeFt(primary, read));
    if (d < distance) { distance = d; measuring = summon; }
  }
  // the reach band reads the MEASURING body's reach (is_target_in_range swaps $caster_arr to the summon)
  const reach = read.value(measuring, 'reach');
  const reachFt = reach === null || reach === undefined || reach === '' ? 5 : /^\s*\d+\s*$/.test(String(scalar(reach))) ? Number(scalar(reach)) : null;
  let allowed: number; let word: 'range' | 'reach' = 'range';
  if (/^\s*\d+\s*$/.test(String(range ?? ''))) allowed = Number(range);
  else if (upper(range) === RANGE_UNLIMITED) return null;
  else if (upper(range) === RANGE_REACH) { allowed = -1; word = 'reach'; }
  else if (RANGE_FT[upper(range)] !== undefined) allowed = RANGE_FT[upper(range)]!;
  else return undefined;
  if (allowed <= 15) {
    if (reachFt === null) return undefined; // a tagged reach the page does not resolve
    allowed = reachFt * multiplier; // the multiplier applies to the BASE reach (no move-towards bonus here)
  } else allowed *= multiplier;
  return distance <= allowed ? null : `Target is out of ${word} by ${shortBy(distance - allowed)} ft.`;
}

/** The Bouncing block of validate_and_adjust_targets(), in PHP's order. */
export function bounceGeometryVerdict(input: BounceInput, read: CastGeometryReaders): CastGeometryVerdict {
  const { caster, ability, tokens, targets } = input;
  if (tokens.length !== 2 || !bounceEligible(caster, ability, input.variable1, read)) return { kind: 'not-bounce' };
  const [primaryToken, secondaryToken] = tokens as [string, string];
  const [primary, secondary] = targets;
  // PHP drops an unseen selection before this block (the directed-visibility filter): only a pair the page sees CLEARLY is judged
  // here, so the pair PHP would judge is the pair the page judges.
  if (!UUID.test(primaryToken) || !primary || !record(primary.encounter_creatures)) return { kind: 'unknown', site: 'bounce-primary-row' };
  if (read.visibility(caster, primary) !== 'visible') return { kind: 'unknown', site: 'bounce-visibility' };
  if (!UUID.test(secondaryToken)) {
    // A map point as the second token (the synthetic encounter row PHP builds for it) is never a creature.
    return secondaryToken.includes('|') ? { kind: 'refuse', message: BOUNCE_NEEDS_CREATURES } : { kind: 'unknown', site: 'bounce-secondary-row' };
  }
  if (!secondary) return { kind: 'unknown', site: 'bounce-secondary-row' };
  if (!record(secondary.encounter_creatures)) return { kind: 'unknown', site: 'bounce-secondary-row' };
  if (read.visibility(caster, secondary) !== 'visible') return { kind: 'unknown', site: 'bounce-visibility' };
  const range = primaryRangeRefusal(caster, primary, ability, read, input.roster);
  if (range === undefined) return { kind: 'unknown', site: 'bounce-range' };
  if (range !== null) return { kind: 'refuse', message: range };
  // calculate_range_from_caster_to_target(primary, secondary): the primary is the measuring body.
  const primarySize = combatSizeFt(primary, read);
  const a = combatCenter(primary), b = combatCenter(secondary);
  if (primarySize === null || !a || !b || has(read, secondary, 'swarm_width_lanes')) return { kind: 'unknown', site: 'bounce-gap' };
  return combatDistanceFt(a, b, primarySize, combatSizeFt(secondary, read)) > 10 ? { kind: 'refuse', message: BOUNCE_GAP } : { kind: 'proven' };
}

export const CHAIN_NEEDS_CREATURES = 'Each leap target must be a creature.';
export const chainGapMessage = (ft: number): string => `Each leap target must be within ${ft} ft of the primary target (the first one picked).`;
/** chain_targets_max as the extra picks a CHAIN host allows (ability/rolls/chain-leap.ts, 2026-10-07); 0 = no chain. */
export function chainExtraTargets(ability: Row, read: Pick<AbilityPreconditionReaders, 'value'>): number {
  const max = Math.trunc(Number(scalar(read.value(ability, 'chain_targets_max')) ?? 0));
  return Number.isFinite(max) && max >= 1 ? max : 0;
}
/** The CHAIN geometry (chain_targets_max / chain_distance_ft — chain-leap.ts, R-175): every pick after the first (the primary) must be a
 *  creature within chain_distance_ft of the primary, measured v3 like Bouncing's gap. 'not-bounce' = no chain picks to judge. */
export function chainGeometryVerdict(input: BounceInput, read: CastGeometryReaders): CastGeometryVerdict {
  const { ability, tokens, targets } = input;
  if (chainExtraTargets(ability, read) < 1 || tokens.length < 2) return { kind: 'not-bounce' };
  const primary = targets[0];
  if (!UUID.test(tokens[0]!) || !primary || !record(primary.encounter_creatures)) return { kind: 'unknown', site: 'chain-primary-row' };
  const rawLimit = Math.trunc(Number(scalar(read.value(ability, 'chain_distance_ft')) ?? 0));
  const limit = Number.isFinite(rawLimit) ? Math.max(0, rawLimit) : 0;
  const primarySize = combatSizeFt(primary, read);
  const a = combatCenter(primary);
  if (primarySize === null || !a || has(read, primary, 'swarm_width_lanes')) return { kind: 'unknown', site: 'chain-gap' };
  for (let i = 1; i < tokens.length; i++) {
    const token = tokens[i]!;
    const leap = targets[i];
    if (!UUID.test(token)) return token.includes('|') ? { kind: 'refuse', message: CHAIN_NEEDS_CREATURES } : { kind: 'unknown', site: 'chain-leap-row' };
    if (!leap || !record(leap.encounter_creatures)) return { kind: 'unknown', site: 'chain-leap-row' };
    const b = combatCenter(leap);
    if (!b || has(read, leap, 'swarm_width_lanes')) return { kind: 'unknown', site: 'chain-gap' };
    if (combatDistanceFt(a, b, primarySize, combatSizeFt(leap, read)) > limit) return { kind: 'refuse', message: chainGapMessage(limit) };
  }
  return { kind: 'proven' };
}

const SPELL_LIKE = new Set(['spell', 'curse', 'menta']);
const ATTACK_TYPES = new Set(['attack_ability', 'opportunity_attack_ability']);
const PHYSICAL_TYPES = new Set(['weapon', 'natural_weapon', 'attack_ability', 'opportunity_attack_ability']);
/** RollType weapon_melee_attack_roll / weapon_ranged_attack_roll. */
const WEAPON_ATTACK_ROLLS = new Set(['2FE8DFB7-FE61-4862-AF71-3523262350A0', '3F85CA97-F9B2-A83A-FDE8-E523FD99346C']);
const itemType = (row: Row): string => String(record(row.items)?.item_type ?? row.item_type ?? '');
const isNumeric = (v: unknown): boolean => /^\s*-?\d+(\.\d+)?\s*$/.test(String(scalar(v) ?? ''));

/** $is_aoe_or_paoe_ability: the ability (or its attack context) carries an area. */
function isArea(ability: Row, read: AbilityPreconditionReaders): boolean {
  return [ability, record(ability.attack_ability_arr)].some(layer => !!layer
    && (read.value(layer, 'aoe_type') != null || present(read.value(layer, 'persistent_aoe'))));
}

/**
 * The SELECTION's sight (validate_and_adjust_targets §5 "directed visibility", deleted from PHP for a player cast 2026-09-29, R-103): a
 * creature the caster does not see is DROPPED from a directed cast; a physical strike keeps a touch-located one (PHP still stamps its
 * DIS marker); when every picked creature is dropped → "You cannot target into an obscured area.". Area casts are exempt (their aim is
 * the stage's; occupants are incidental). An unjudgeable read (null) keeps the pick. Returns the ids to drop + their names.
 */
export function selectionVisibilityVerdict(input: { caster: Row; ability: Row; targets: Array<{ id: string; row: Row | null }> },
  read: CastGeometryReaders): { kind: 'refuse'; message: string } | { kind: 'keep'; drop: string[]; names: string[] } {
  const { caster, ability, targets } = input;
  if (isArea(ability, read)) return { kind: 'keep', drop: [], names: [] };
  const casterId = upper(record(caster.encounter_creatures)?.encounter_creature_id);
  // janus_item_uses_physical_weapon_attack_roll(): a weapon / attack item, or a weapon melee / ranged attack roll on the ability or its
  // attack context (a quick_ability striking with a weapon roll type).
  const physical = PHYSICAL_TYPES.has(itemType(ability))
    || [ability, record(ability.attack_ability_arr)].some(layer => !!layer && WEAPON_ATTACK_ROLLS.has(upper(read.value(layer, 'roll_type'))));
  const drop: string[] = []; const names: string[] = []; let judged = 0;
  for (const { id, row } of targets) {
    if (!row || !record(row.encounter_creatures) || upper(id) === casterId) continue; // not a creature pick / the caster: not sight-gated here
    judged++;
    const state = read.visibility(caster, row);
    if (state === null || state === 'visible' || (state === 'touch_located' && physical)) continue;
    drop.push(upper(id));
    names.push(String(record(row.encounter_creatures)?.encounter_creature_name ?? record(row.player_characters)?.player_name ?? 'an obscured target'));
  }
  if (drop.length && drop.length === judged && judged === targets.length) return { kind: 'refuse', message: 'You cannot target into an obscured area.' };
  return { kind: 'keep', drop, names };
}

/**
 * The TARGET-COUNT ceiling for a player's cast (validate_and_adjust_targets' max-targets / upcast / single-target / consumable vetoes,
 * deleted from PHP for a player cast 2026-09-29, R-103). The INTENDED rule, not PHP's two precedence quirks
 * (`!layer_value(…'spell_upcast_additional_targets_int') !== null` is always true, so PHP refused the extra targets an upcast grants):
 *   - an attack ability allows several targets only with an area, `[x]` or max_targets ≥ 2 (else 1);
 *   - an authored numeric max_targets (+1 Twin / Bouncing on a max of 1); an upcast adds spell_upcast_additional_targets_int per level;
 *   - `[x]` allows as many as the invested amount (variable_1);
 *   - no max_targets on a ranged, non-area ability → one target (+1 Twin / Bouncing), plus the upcast allowance;
 *   - a range-less consumable / potion / elixir / food / drink in a campaign → one target.
 * A map-point (area) selection and a Self-range direct cast (forced onto the caster) are never counted here. Null = allowed.
 */
export function targetCountRefusal(input: { caster: Row; ability: Row; count: number; variable1?: unknown; inCampaign: boolean; mapPoint: boolean;
  inventoryPicker: boolean }, read: AbilityPreconditionReaders): string | null {
  const { caster, ability, count, variable1 } = input;
  if (count <= 1 || input.mapPoint) return null;
  const type = itemType(ability);
  const range = read.value(ability, 'range');
  const area = isArea(ability, read);
  if (upper(range) === RANGE_SELF && !area && !input.inventoryPicker) return null; // forced onto the caster
  if (input.inCampaign && (range === null || range === undefined) && (has(read, ability, 'consumable') || ['potion', 'elixir', 'food', 'drink'].includes(type)))
    return `This ${type} can only have a single target for it to be consumed; try again and select yourself or just one target.`;
  const extra = sanguineExtraTargets(caster, ability, variable1, read);
  const v1 = scalar(variable1);
  const v1Numeric = isNumeric(v1);
  const spellLike = SPELL_LIKE.has(type);
  const level = Number(scalar(read.value(ability, 'spell_level') ?? read.value(ability, 'menta_level') ?? 0));
  const addPerLevel = read.value(ability, 'spell_upcast_additional_targets_int');
  const upcastBonus = spellLike && v1Numeric && level > 0 && addPerLevel != null && isNumeric(addPerLevel)
    ? Math.max(0, Number(scalar(addPerLevel)) * (Number(v1) - level)) : null;
  // A CHAIN host (chain_targets_max, R-175): the primary + up to N leap targets — an absolute ceiling (resolution takes no more; an
  // authored max_targets / upcast / Twin bonus does not widen it).
  const chain = chainExtraTargets(ability, read);
  if (chain > 0) return count <= 1 + chain ? null
    : `This ${type} only allows ${1 + chain} targets (the first one picked, then up to ${chain} it leaps to). You have selected ${count} targets.`;
  let max: unknown = scalar(read.value(ability, 'max_targets'));
  if (ATTACK_TYPES.has(type)) {
    const multi = area || max === '[x]' || (isNumeric(max) && Math.trunc(Number(max)) >= 2);
    if (!multi) max = 1;
  }
  if (max === '[x]') {
    return spellLike && v1Numeric && count > Number(v1) ? `This ${type} only allows ${Number(v1)} targets. You have selected ${count} targets.` : null;
  }
  if (isNumeric(max)) {
    const base = Math.trunc(Number(max));
    const ceiling = (base === 1 || upcastBonus !== null ? base + extra : base) + (upcastBonus ?? 0);
    if (count <= ceiling) return null;
    return upcastBonus !== null
      ? `This upcastable ${type} only allows ${ceiling} targets. You have selected ${count} targets.`
      : `This ${type} only allows ${ceiling} targets. You have selected ${count} targets.`;
  }
  if (range === null || range === undefined || area) return null;
  // PHP gated the implicit single-target rule on variable_1: empty, an innate / at-will marker, or a numeric level.
  if (v1 !== null && v1 !== undefined && v1 !== '' && !v1Numeric && !['innate_spell_per_long_rest', 'at_will_spell'].includes(String(v1))) return null;
  const ceiling = 1 + extra + (upcastBonus ?? 0);
  if (count <= ceiling) return null;
  return upcastBonus !== null
    ? `This upcastable ${type} only allows ${ceiling} targets using this much mana. You have selected ${count} targets.`
    : `This ${type} only allows one target. Please select only 1 target.`;
}

const PILOTED_STATUS = 'DDC66C31-4093-84AF-18BB-093D924B8738';
/** Spot Hidden Creatures / Search for Hidden Inventory: their area holds every tile; resolution contests only the hidden ones. */
const ITEM_TYPE_GATE_EXEMPT = new Set(['B2D3417B-2700-11CB-E17D-38DECAF66F1A', '5B518ED7-4CEF-72BE-F20B-912598267714']);
const LOWERCASE_WORDS = new Set(['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'of', 'on', 'at', 'to', 'from', 'by', 'in', 'with']);
/** ucwords_and_space(): underscores → spaces, title case, small words lower (the first always capitalised). */
function titleWords(value: string): string {
  const words = value.trim().toLowerCase().replace(/_/g, ' ').split(' ');
  return words.map((word, i) => (i === 0 || !LOWERCASE_WORDS.has(word) ? word.charAt(0).toUpperCase() + word.slice(1) : word)).filter(Boolean).join(' ');
}
const escapeHtml = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** FT_CASTER_NON_CARRYABLE_ITEM_TYPES (get_weapon_for_attack_ability.php): map-placed types, never rows in a backpack. */
const NON_CARRYABLE_ITEM_TYPES = new Set(['furniture', 'door', 'building', 'window', 'wall', 'roof', 'floor',
  'vehicle_land', 'vehicle_water', 'vehicle_air', 'war_machine', 'ore_vein', 'tree', 'trap', 'digsite']);
const TEAM_TARGET_LAYERS = ['effect_only_affects_opposing_team_type', 'effect_only_affects_same_team_type', 'target_opposing_team_only', 'target_friendly_only'];
const itemTypeGate = (ability: Row, read: AbilityPreconditionReaders): string[] =>
  (read.entries?.(ability, 'ability_works_only_against_item_type') ?? []).map(entry => String(scalar(entry) ?? '').trim()).filter(Boolean);
/** ability_uses_caster_inventory_target_picker(): an item-type gate with no team layer that names some carryable gear. */
export function usesCasterInventoryPicker(ability: Row, read: AbilityPreconditionReaders): boolean {
  const types = itemTypeGate(ability, read);
  return types.length > 0 && !TEAM_TARGET_LAYERS.some(layer => read.has(ability, layer))
    && types.some(type => !NON_CARRYABLE_ITEM_TYPES.has(type.toLowerCase()));
}

export interface TargetRuleTarget {
  row: Row | null;
  /** The item_type the target resolves to (a creature row's, or an inventory row's template) — null when the page cannot tell. */
  itemType: string | null;
  name: string;
  /** Held-item targets only: the creature holding it (null = none on the page), carried by the caster, equipped. */
  holder?: { name: string } | null;
  carriedByCaster?: boolean;
  equipped?: boolean;
}

/**
 * The per-target rule gates validate_and_adjust_targets() ran on a player's selection (cast-gate session 2026-09-29, R-103 — deleted from
 * PHP for a player cast), in PHP's order with PHP's words:
 *   1. ability_works_only_against_item_type — every target must be one of the listed item types (Spot Hidden Creatures / Search for
 *      Hidden Inventory exempt); a target whose type the page cannot read is not refused;
 *   2. an item another creature holds must be EQUIPPED — reach never covers the inside of their bags;
 *   3. a Piloted-applying ability aimed at a war_machine needs both of the caster's hands free.
 */
/** A target that is operated in place (war_machine, or an item carrying operable_object). The row's layer read resolves an
 *  inventory row through its catalog template (ftLayerValue → _ftInventoryTemplateDef). */
const operableTarget = (target: TargetRuleTarget, read: AbilityPreconditionReaders): boolean =>
  isOperableObject(target.itemType, target.row ? read.value(target.row, OPERABLE_OBJECT_LAYER) : null);

export function targetRuleRefusal(input: { caster: Row; ability: Row; targets: TargetRuleTarget[] }, read: AbilityPreconditionReaders): string | null {
  const { caster, ability, targets } = input;
  const abilityId = upper(record(ability.items)?.item_id ?? ability.item_id);
  const allowed = itemTypeGate(ability, read);
  if (allowed.length && !ITEM_TYPE_GATE_EXEMPT.has(abilityId)) {
    for (const target of targets) {
      if (target.itemType === null || itemTypeGateAdmits(allowed, target.itemType, operableTarget(target, read))) continue;
      const name = escapeHtml(String(record(ability.items)?.item_name ?? ability.item_name ?? 'this ability'));
      return `You cannot use <strong>${name}</strong> on ${escapeHtml(target.name)}.<br>This ability only works on: <strong>${allowed.map(titleWords).join(', ')}</strong>.`;
    }
  }
  if (usesCasterInventoryPicker(ability, read)) {
    for (const target of targets) {
      if (target.carriedByCaster || !target.holder || target.equipped !== false) continue;
      return `You can only reach an item ${escapeHtml(target.holder.name)} has equipped, not one stowed in their gear.`;
    }
  }
  const applied = (read.entries?.(ability, 'status_effect_applied_to_target_after_cast') ?? []).map(entry => upper(scalar(entry)));
  const operated = applied.includes(PILOTED_STATUS) ? targets.find(target => operableTarget(target, read)) : undefined;
  if (operated) {
    const handsFull = Number(scalar(read.value(caster, 'count_main_hand_equipped') ?? 0)) > 0 || Number(scalar(read.value(caster, 'count_off_hand_equipped') ?? 0)) > 0;
    if (handsFull) return operated.itemType === 'war_machine'
      ? 'You need both hands free to work a war machine. Unequip what you are holding first.'
      : `You need both hands free to operate ${escapeHtml(operated.name)}. Unequip what you are holding first.`;
  }
  return null;
}

/**
 * A creature-anchored AREA (a follow aura, no map point): the explicitly selected anchor must be SEEN, and a Burrowed anchor must be in the
 * caster's tremorsense (the surface line is the scene's — campaignBurrowedAnchorLine). Twin of the anchor block validate_and_adjust_targets
 * ran for a player (R-103, deleted). Returns PHP's refusal, 'scene' when a Burrowed anchor needs the scene's line, or null.
 */
export function areaAnchorVerdict(input: { caster: Row; ability: Row; anchors: Row[]; burrowed: (row: Row) => boolean; tremorsenseReaches: (row: Row) => boolean },
  read: CastGeometryReaders): { kind: 'refuse'; message: string } | { kind: 'scene'; anchors: Row[] } | null {
  if (!isArea(input.ability, read)) return null;
  const casterId = upper(record(input.caster.encounter_creatures)?.encounter_creature_id);
  // ft_spatial_action_policy(): an item authored to ignore line of effect ignores the walls above a Burrowed anchor too.
  const ignoresLine = [input.ability, record(input.ability.attack_ability_arr)].some(layer => !!layer && present(read.value(layer, 'ignores_line_of_effect')));
  const scene: Row[] = [];
  for (const anchor of input.anchors) {
    const anchorId = upper(record(anchor.encounter_creatures)?.encounter_creature_id);
    if (!anchorId || anchorId === casterId) continue; // an inventory anchor / the caster: not sight-gated here
    const state = read.visibility(input.caster, anchor);
    // PHP refused an anchor it could not judge (no senses context) — this is the only check left for a player's anchor (Codex review).
    if (state === null) return { kind: 'refuse', message: 'Encounter geometry is unavailable, so the aim cannot be judged.' };
    if (state !== 'visible') return { kind: 'refuse', message: 'You cannot target into an obscured area.' };
    if (!input.burrowed(anchor)) continue;
    if (!input.tremorsenseReaches(anchor)) return { kind: 'refuse', message: 'The line of effect to that target is blocked.' };
    if (!ignoresLine) scene.push(anchor);
  }
  return scene.length ? { kind: 'scene', anchors: scene } : null;
}
